import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGuestAuth } from '@/common/contexts/GuestAuthContext';
import { useLanguageData } from '@/common/hooks/useLanguageData';
import { checkActive } from '@/api/query/customer';
import { STUDIO_BRAND_LOGO } from '@/common/constants/studioPublicMedia';
import {
	AUTH_REGISTER_TALL,
	AUTH_REGISTER_WIDE,
} from '@/common/constants/authPublicMedia';
import AuthForm from './AuthForm/AuthForm';
import {
	AuthBrand,
	AuthCard,
	AuthChrome,
	AuthClose,
	AuthFormBlock,
	AuthFormTop,
	AuthHeadline,
	AuthInlineHeart,
	AuthKicker,
	AuthLogo,
	AuthMain,
	AuthOverlay,
	AuthPanel,
	AuthSwitchButton,
	AuthTagline,
	AuthTitle,
	AuthVisual,
	AuthVisualTall,
	AuthVisualVeil,
	AuthVisualWide,
} from './authModal.styles';

const IconHeart = () => (
	<svg
		className="auth-inline-heart"
		width="14"
		height="13"
		viewBox="0 0 64 58"
		fill="none"
		aria-hidden="true"
	>
		<path
			d="M32 52S6 36 6 18.5C6 9.5 13 4 20.5 4 26 4 30 7.5 32 12c2-4.5 6-8 11.5-8C51 4 58 9.5 58 18.5 58 36 32 52 32 52Z"
			stroke="#ff4df2"
			strokeWidth="4"
			strokeLinejoin="round"
		/>
	</svg>
);

const AuthModal = ({ tab = 'register', onClose }) => {
	const navigate = useNavigate();
	const { refreshAuthState, authReturnPath, clearAuthReturnPath, openPlanPaywall } =
		useGuestAuth();
	const t = useLanguageData('authModal');
	const [activeTab, setActiveTab] = useState(tab);

	useEffect(() => {
		setActiveTab(tab);
	}, [tab]);

	const handleSuccess = async () => {
		const currentPath = `${window.location.pathname}${window.location.search}`;
		const destination = authReturnPath ?? currentPath;
		const isStudioAuthFlow = destination.startsWith('/studio');

		await refreshAuthState();
		onClose();
		clearAuthReturnPath();

		if (isStudioAuthFlow) {
			try {
				const { data } = await checkActive();
				const skipPlanGate =
					new URLSearchParams(window.location.search).get('autoGenerate') ===
					'1';

				if (!data?.isActive && !skipPlanGate) {
					openPlanPaywall(destination);
					return;
				}
			} catch {
				const skipPlanGate =
					new URLSearchParams(window.location.search).get('autoGenerate') ===
					'1';

				if (!skipPlanGate) {
					openPlanPaywall(destination);
					return;
				}
			}
		}

		if (destination !== currentPath) {
			navigate(destination);
		}
	};

	const isRegister = activeTab === 'register';
	const isLogin = !isRegister;

	const handleSwitchMode = () => {
		setActiveTab(isRegister ? 'login' : 'register');
	};

	return (
		<AuthOverlay onClick={onClose}>
			<AuthCard
				role="dialog"
				aria-modal="true"
				aria-labelledby="auth-title"
				onClick={(event) => event.stopPropagation()}
			>
				<AuthVisual aria-hidden="true">
					<AuthVisualWide src={AUTH_REGISTER_WIDE} alt="" />
					<AuthVisualWide $blur src={AUTH_REGISTER_WIDE} alt="" />
					<AuthVisualTall src={AUTH_REGISTER_TALL} alt="" />
					<AuthVisualTall $blur src={AUTH_REGISTER_TALL} alt="" />
					<AuthVisualVeil />
				</AuthVisual>
				<AuthPanel>
					<AuthChrome>
						<AuthClose type="button" onClick={onClose} aria-label={t.close}>
							×
						</AuthClose>
					</AuthChrome>
					<AuthBrand $isLogin={isLogin}>
						<AuthLogo src={STUDIO_BRAND_LOGO} alt="Soft AI" />
						<AuthKicker>{t.brandKicker}</AuthKicker>
						<AuthHeadline>{t.brandHeadline}</AuthHeadline>
						<AuthTagline>
							{t.brandTagline}{' '}
							<AuthInlineHeart>
								<IconHeart />
							</AuthInlineHeart>
						</AuthTagline>
					</AuthBrand>
					<AuthFormBlock>
						<AuthFormTop>
							<div>
								<AuthTitle id="auth-title">
									{isRegister ? t.registerTitle : t.loginTitle}
								</AuthTitle>
							</div>
							<AuthSwitchButton type="button" onClick={handleSwitchMode}>
								{isLogin ? (
									<>
										{t.noAccountPrompt}{' '}
										<span>{t.switchToRegister}</span>
									</>
								) : (
									<>
										{t.hasAccountPrompt}{' '}
										<span>{t.switchToLogin}</span>
									</>
								)}
							</AuthSwitchButton>
						</AuthFormTop>
						<AuthMain>
							<AuthForm
								mode={activeTab}
								onSuccess={handleSuccess}
							/>
						</AuthMain>
					</AuthFormBlock>
				</AuthPanel>
			</AuthCard>
		</AuthOverlay>
	);
};

export default AuthModal;
